import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PushSchalter from '../PushSchalter';
import { useTranslation } from 'react-i18next';
import { useVerband } from '../../context/VerbandContext';
import LanguageSwitch from '../LanguageSwitch';
import WettkaempferRegistrieren from './WettkaempferRegistrieren';
import VereineListePage from '../../pages/Club/VereineListePage';
import VereinDetailPage from '../../pages/Club/VereinDetailPage';
import VereinCreatePage from '../../pages/Club/VereinCreatePage';
import TurnierBearbeitenPage from '../../pages/Turniere/TurnierBearbeitenPage';
// import TurnierListe from '../turnier/TurnierListe';
// import LiveStreamingManager from '../streaming/LiveStreamingManager';
import SportsManager from '../sports/SportsManager';
import TurnierEinstellungen from '../../pages/Organisation/TurnierEinstellungen';
import WettkampftagChecklist from './WettkampftagChecklist';
import './MainDashboard.css';
import { CURRENT_VERSION, CURRENT_BUILD_DATE } from '../../version';
import { CHANGELOG } from '../../changelog';

const WettkaempferDashboard       = React.lazy(() => import('./WettkaempferDashboard'));
const BracketDashboard            = React.lazy(() => import('./BracketDashboard'));
const TurnierDashboard            = React.lazy(() => import('./TurnierDashboard'));
const StreamingDashboard          = React.lazy(() => import('./StreamingDashboard'));
const AnmeldeDashboard            = React.lazy(() => import('./AnmeldeDashboard'));
const EinzelstarterDashboard      = React.lazy(() => import('./EinzelstarterDashboard'));
const VereinsmailsDashboard       = React.lazy(() => import('./VereinsmailsDashboard'));
const AnalyticsDashboard          = React.lazy(() => import('./AnalyticsDashboard'));
const DivisionDashboard           = React.lazy(() => import('./DivisionDashboard'));
const FormenDashboard             = React.lazy(() => import('./FormenDashboard'));
const SelbstverteidigungDashboard = React.lazy(() => import('./SelbstverteidigungDashboard'));
const MattenWettkampfDashboard    = React.lazy(() => import('./MattenWettkampfDashboard'));
const BruchtestDashboard          = React.lazy(() => import('./BruchtestDashboard'));
const KampfflaechenManager        = React.lazy(() => import('./KampfflaechenManager'));
const StaffManager                = React.lazy(() => import('./StaffManager'));
const ZeitplanDashboard           = React.lazy(() => import('./ZeitplanDashboard'));
const EventsDashboard             = React.lazy(() => import('./EventsDashboard'));
const BerichteDashboard           = React.lazy(() => import('./BerichteDashboard'));
const LiveSpectatorDashboard      = React.lazy(() => import('./LiveSpectatorDashboard'));
const ErgebnisDashboard           = React.lazy(() => import('./ErgebnisDashboard'));
const SaisonRanglisteDashboard    = React.lazy(() => import('./SaisonRanglisteDashboard'));
const EinwaageDashboard           = React.lazy(() => import('./EinwaageDashboard'));
const CheckinDashboard            = React.lazy(() => import('./CheckinDashboard'));
const AkuendigungenDashboard      = React.lazy(() => import('./AkuendigungenDashboard'));
const AkkreditierungDashboard     = React.lazy(() => import('./AkkreditierungDashboard'));
const DruckDashboard              = React.lazy(() => import('./DruckDashboard'));
const SchnellregistrierungModal   = React.lazy(() => import('./SchnellregistrierungModal'));
const RegStaffManager             = React.lazy(() => import('./RegStaffManager'));
const KampfrichterManager         = React.lazy(() => import('./KampfrichterManager'));
const RefereeManager             = React.lazy(() => import('../referee/RefereeManager'));
const WeighStaffManager           = React.lazy(() => import('./WeighStaffManager'));
const VerbaendeDashboard          = React.lazy(() => import('./VerbaendeDashboard'));
const ModusUebersichtDashboard    = React.lazy(() => import('./ModusUebersichtDashboard'));
const PlatzierungDashboard        = React.lazy(() => import('./PlatzierungDashboard'));

const LS_VERSION_KEY = 'tda-version-seen';

// Mapping: alte activeView-IDs → BerichteDashboard report-IDs
const BERICHT_VIEW_MAP = {
  'bericht-mitgliedschaft':       'vereine',
  'bericht-offizielle':           'offizielle',
  'event-schule-bericht':         'vereine',
  'staff-checkin-bericht':        'staff',
  'staff-aktivitaetenbericht':    'staff',
  'kampfrichter-manager':         'kampfrichter-manager',
  'rings-berichte':               'ringe',
  'bericht-wettkaempfer':         'neue-wettkaempfer',
  'teilnehmerbericht':            'teilnehmer',
  'restrictions-bericht':         'divisionen',
  'medizinisches-formular-bericht': 'medizinisch',
  'medizinische-notizen-bericht': 'medizinisch',
  'finanzbericht':                'finanzen',
  'bericht-ergebnisse':           'ergebnisse',
  'berichte':                     null,
  'medaillenbericht':             'medaillen',
  'bericht-teilzahlungen':        'teilzahlungen',
};

function MainDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { verband, activeVerband, setActiveVerband } = useVerband() || {};

  // Bestimme activeView aus der URL
  const getViewFromPath = () => {
    const path = location.pathname.replace('/dashboard', '').replace('/', '');
    return path || 'overview';
  };

  const [activeView, setActiveView] = useState(getViewFromPath());
  const [modusNavState] = useState(null); // { initialTab, initialModus }
  const [user, setUser] = useState(null);
  // Zwei-Faktor-Stand des eigenen Kontos, für den Hinweis oben (26.09.2026).
  const [zweiFaktor, setZweiFaktor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [showVerbandDropdown, setShowVerbandDropdown] = useState(false);
  const [allVerbaende, setAllVerbaende] = useState([]);
  const [showHelp, setShowHelp] = useState(false);
  const [helpTab, setHelpTab] = useState('hilfe');
  const [staffTab, setStaffTab] = useState('personal');
  const [hasNewVersion, setHasNewVersion] = useState(false);
  const [selectedTurnier, setSelectedTurnier] = useState(null);
  const [events, setEvents] = useState([]);   // Lehrgänge/Seminare/Prüfungen/Camps
  const [turniere, setTurniere] = useState([]);
  // Verhindert null-Reset von selectedTurnier beim ersten Verband-Load
  const verbandInitializedRef = React.useRef(false);
  // Verhindert parallele/doppelte fetchTurniere-Aufrufe
  const fetchingRef = React.useRef(false);
  const [ungeleseneAnnouncements, setUngeleseneAnnouncements] = useState(0);

  // Theme: dark | light (Washi)
  const [theme, setTheme] = useState(() => localStorage.getItem("tda-theme") || "light");

  // Theme auf HTML-Element setzen + in localStorage speichern
  useEffect(() => {
    if (!user) return;
    fetch('/api/2fa/status')
      .then(r => (r.ok ? r.json() : null))
      .then(d => setZweiFaktor(d))
      .catch(() => {});
  }, [user]);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("tda-theme", theme);
  }, [theme]);

  const toggleTheme = () => setTheme(prev => prev === "dark" ? "light" : "dark");

  // Funktion zum Wechseln der Ansicht mit URL-Update
  const changeView = useCallback((view) => {
    setActiveView(view);
    const path = view === 'overview' ? '/dashboard' : `/dashboard/${view}`;
    navigate(path);
  }, [navigate]);

  // Synchronisiere activeView mit URL-Änderungen (z.B. Zurück-Button)
  useEffect(() => {
    const path = location.pathname.replace('/dashboard', '').replace('/', '');
    const viewFromPath = path || 'overview';
    setActiveView(prev => prev !== viewFromPath ? viewFromPath : prev);
  }, [location.pathname]);

  const handleLogout = useCallback(() => {
      ['token', 'authToken', 'adminData', 'clubData'].forEach(k => localStorage.removeItem(k));
    setUser(null);
    navigate('/');
  }, [navigate]);

  // Formen-Schreiber in neuem Fenster öffnen
  const selectedTurnierId = selectedTurnier?.turnier_id ?? selectedTurnier?.id ?? null;
  const openFormenWindow = useCallback(() => {
    if (!selectedTurnierId) {
      alert(t('admin.common.select_tournament_first'));
      return;
    }
    const url = `/formen-schreiber?turnierId=${selectedTurnierId}`;
    window.open(url, 'FormenSchreiber', 'width=1600,height=900,menubar=no,toolbar=no,location=no,status=no');
  }, [selectedTurnierId, t]);

  const checkAuthStatus = useCallback(() => {
    try {
      const adminData = localStorage.getItem('adminData');
      const clubData = localStorage.getItem('clubData');
      const token = localStorage.getItem('token');

      // Check if token exists (accept both JWT and dummy tokens)
      if (!token || token.trim() === '') {
        console.log('🔑 Invalid or missing token - clearing localStorage');
        handleLogout();
        return;
      }

      // Accept both JWT format and dummy tokens (admin-auth-token, user-auth-token)
      const isJWT = token.split('.').length === 3;
      const isDummyToken = token.startsWith('admin-auth-token') || token.startsWith('user-auth-token');

      if (!isJWT && !isDummyToken) {
        console.log('🔑 Invalid token format - clearing localStorage');
        handleLogout();
        return;
      }

      if (adminData) {
        const admin = JSON.parse(adminData);
        const adminUser = { ...admin.user, isAdmin: true };
        setUser(adminUser);
        // Verbände für Super-Admin laden
        if (adminUser.verband_id_tenant === null || adminUser.verband_id_tenant === undefined) {
          fetch('/api/verbaende', { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
            .then(r => r.json())
            .then(d => { if (d.success) setAllVerbaende(d.data); })
            .catch(() => {});
        }
      } else if (clubData) {
        const club = JSON.parse(clubData);
        // Verein-Nutzer gehören nicht ins Admin-Dashboard
        if (club.user?.role !== 'admin') {
          navigate('/verein', { replace: true });
          return;
        }
        setUser({ ...club.user, isAdmin: false });
      } else {
        navigate('/');
        return;
      }
      // setLoading(false) wird von fetchTurniere() übernommen
      // damit User + Turnierdaten in einem einzigen Render erscheinen
    } catch (error) {
      console.error('Auth-Fehler:', error);
      handleLogout();
    }
  }, [navigate, handleLogout]);

  useEffect(() => {
    checkAuthStatus();
    fetchTurniere();  // Initialer Einmal-Load beim Mount
    const token = localStorage.getItem('token');
    if (token) {
      fetch('/api/ankuendigungen', { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.json())
        .then(d => { if (d.success) setUngeleseneAnnouncements(d.ungelesen || 0); })
        .catch(() => {});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Events (Lehrgänge/Camps) für Header-Dropdown laden
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch('/api/events', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d && d.success) setEvents(d.data || []); })
      .catch(() => {});
  }, []);

  const fetchTurniere = useCallback(async () => {
    if (fetchingRef.current) return; // Doppel-Aufruf verhindern
    fetchingRef.current = true;
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/turniere', {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const result = await response.json();
        const list = result.success && result.data ? result.data : Array.isArray(result) ? result : null;
        if (list) {
          setTurniere(list);
          if (list.length > 0) {
            // Funktionales Update: nur neue Referenz setzen wenn sich die ID wirklich geändert hat.
            // Verhindert unnötige Re-Renders aller Kind-Komponenten bei gleichem Turnier.
            setSelectedTurnier(prev => {
              if (prev) return prev; // Bereits gesetzt → nicht überschreiben
              // Zuletzt gewähltes Turnier aus localStorage wiederherstellen
              const savedId = localStorage.getItem('tda-selected-turnier-id');
              if (savedId) {
                const saved = list.find(t => String(t.turnier_id || t.id) === savedId);
                if (saved) return saved;
              }
              // Kein gespeichertes → erstes kommendes Turnier
              const today = new Date(); today.setHours(0,0,0,0);
              const upcoming = list.find(t => new Date(t.datum) >= today);
              return upcoming || list[0] || null;
            });
          }
        }
      }
    } catch (error) {
      console.error('Fehler beim Laden der Turniere:', error);
    } finally {
      // Erst hier loading=false: User + Turnierdaten erscheinen gemeinsam in einem Render
      setLoading(false);
      fetchingRef.current = false; // Freigeben für nächsten Aufruf (z.B. Verband-Wechsel)
    }
  }, []);

  // Verband-Wechsel: beim ERSTEN Load (verbandInitializedRef=false) kein zweiter fetchTurniere-Aufruf,
  // da der initiale useEffect ihn bereits startet. Nur bei echtem Wechsel reset+reload.
  useEffect(() => {
    if (activeVerband?.id) {
      if (verbandInitializedRef.current) {
        // Echter Verband-Wechsel: Turnier zurücksetzen und neu laden
        setSelectedTurnier(null);
        fetchTurniere();
      }
      verbandInitializedRef.current = true;
      // Kein fetchTurniere() beim ersten Verband-Load — initial useEffect übernimmt das
    }
  }, [activeVerband?.id, fetchTurniere]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.user-dropdown')) {
        setShowUserDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Wenn ein anderer Tab adminData löscht (z.B. Vereins-Login), sofort zur Login-Seite
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'adminData' && event.newValue === null) {
        setUser(null);
        navigate('/');
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [navigate]);

  // "Neu"-Badge: prüfen ob User neue Version noch nicht gesehen hat
  useEffect(() => {
    const seen = localStorage.getItem(LS_VERSION_KEY);
    if (seen !== CURRENT_VERSION) setHasNewVersion(true);
  }, []);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner">⏳</div>
        <p>{t('admin.header.loading')}</p>
      </div>
    );
  }

  return (
    <div className="main-dashboard">
      {/* COMPLETELY REMOVE ALL OLD HEADERS - ONLY ONE UNIFIED HEADER */}
      <div className="unified-dashboard-header">
        <div className="header-content">

          {/* Links: Logo + Version */}
          <div className="header-brand">
            <div className="header-logo-circle">
              <img src={verband?.logo_url || '/logo.webp'} alt={verband?.name || 'Tournament Software'} className="header-logo" />
            </div>
            <span className="header-version">v{CURRENT_VERSION}</span>
          </div>

          {/* Mitte: nur noch Verband-Switcher für Super-Admin */}
          <div className="header-center">
            {user?.verband_id_tenant === null && allVerbaende.length > 0 && (
              <div className="header-verband-switcher" style={{ position: 'relative' }}>
                <button className="header-pill-btn verband-switcher-btn" onClick={() => setShowVerbandDropdown(!showVerbandDropdown)}>
                  <span style={{ fontWeight: 700, fontSize: '0.78rem', letterSpacing: '0.02em', color: 'var(--text-muted, #9a8a6a)' }}>{activeVerband?.name || verband?.name || 'TDA'} ▾</span>
                </button>
                {showVerbandDropdown && (
                  <div className="verband-switcher-dropdown" onClick={() => setShowVerbandDropdown(false)}>
                    {allVerbaende.map(v => (
                      <div key={v.id}
                        className={`verband-switcher-item${activeVerband?.id === v.id ? ' active' : ''}`}
                        onClick={() => {
                          if (setActiveVerband) setActiveVerband(v);
                          localStorage.setItem('activeVerbandId', v.id);
                        }}>
                        <span>{v.name}</span>
                        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          {Number(v.is_demo) === 1 && <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '0.1rem 0.3rem', background: '#78350f', color: '#fbbf24', borderRadius: '3px' }}>DEMO</span>}
                          {activeVerband?.id === v.id && <span style={{ color: 'var(--accent, #3b82f6)' }}>✓</span>}
                        </span>
                      </div>
                    ))}
                    <div className="verband-switcher-divider" />
                    <div className="verband-switcher-item" onClick={() => { changeView('verbaende'); }}>
                      🌐 Verbände verwalten
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Rechts: Aktions-Buttons */}
          <div className="header-actions">
            <button className="header-pill-btn header-pill-btn--red" onClick={() => changeView('overview')}>
              {t('admin.header.btn_dashboard')}
            </button>
            {user?.verband_id_tenant === null && (
              <button className="header-pill-btn header-pill-btn--admin" onClick={() => navigate('/admin')} title="Admin Plattform">
                管理
              </button>
            )}
            <button className="header-pill-btn" onClick={toggleTheme} title={t('admin.header.theme_toggle')}>
              {theme === 'dark' ? t('admin.header.theme_light') : t('admin.header.theme_dark')}
            </button>
            <LanguageSwitch />
            <button className="help-btn" onClick={() => setShowHelp(true)} title="Hilfe & Version">
              ?
              {hasNewVersion && <span className="help-new-dot" />}
            </button>
          </div>

          <div className="header-user">
            <div className="user-dropdown" onClick={() => setShowUserDropdown(!showUserDropdown)}>
              <div className="user-info">
                <span className="user-name">{user?.name || t('admin.header.default_name')}</span>
                {selectedTurnier ? (
                  <span className="user-role user-role--turnier">🏆 {selectedTurnier.name}</span>
                ) : (
                  <span className="user-role">{user?.isAdmin ? t('admin.header.role_admin') : (user?.club_name || t('admin.header.role_club'))}</span>
                )}
                <span className="dropdown-arrow">▼</span>
              </div>

              {showUserDropdown && (
                <div className="dropdown-menu">

                  {/* ── Turnier-Auswahl ──────────────────────────── */}
                  {turniere.length > 0 && (() => {
                    const today = new Date(); today.setHours(0,0,0,0);
                    // Ein Turnier ohne Datum (Dragon Cup 2026: "Termin kommt noch")
                    // ist nicht vergangen -- es steht nur noch nicht fest.
                    // new Date(null) ist der 01.01.1970; ohne diese Unterscheidung
                    // landete es unter VERGANGENE und zeigte 1970 als Datum.
                    const ohneDatum = (tr) => !tr.datum;
                    const upcoming = turniere
                      .filter(tr => ohneDatum(tr) || new Date(tr.datum) >= today)
                      .sort((a, b) => {
                        if (ohneDatum(a) !== ohneDatum(b)) return ohneDatum(a) ? 1 : -1;  // ohne Termin ans Ende
                        if (ohneDatum(a)) return 0;
                        return new Date(a.datum) - new Date(b.datum);
                      });
                    const past = turniere
                      .filter(tr => !ohneDatum(tr) && new Date(tr.datum) < today)
                      .sort((a, b) => new Date(b.datum) - new Date(a.datum));
                    const activeId = selectedTurnier?.id || selectedTurnier?.turnier_id;
                    const fmt = (d) => new Date(d).toLocaleDateString('de-DE', { day:'2-digit', month:'short', year:'numeric' });
                    const datumZeile = (tr) => (tr.datum ? fmt(tr.datum) : (tr.datum_text || 'Termin offen'));
                    const renderRow = (tr) => {
                      const id = tr.id || tr.turnier_id;
                      return (
                        <div key={id}
                          className={`dd-turnier-item${id === activeId ? ' active' : ''}`}
                          onClick={() => { setSelectedTurnier(tr); localStorage.setItem('tda-selected-turnier-id', String(tr.turnier_id || tr.id)); setShowUserDropdown(false); }}>
                          <span className="dd-turnier-name">{tr.name}</span>
                          <span className="dd-turnier-date">{datumZeile(tr)}</span>
                          {id === activeId && <span className="dd-turnier-check">✓</span>}
                        </div>
                      );
                    };
                    return (
                      <>
                        <div className="dropdown-section">
                          <div className="dropdown-label">大会 Turniere</div>
                          <div className="dd-turnier-list">
                            {upcoming.map(renderRow)}
                            {past.length > 0 && upcoming.length > 0 && <div className="dd-turnier-divider">Vergangene</div>}
                            {past.map(renderRow)}
                          </div>
                          <div className="dropdown-item" onClick={() => { changeView('turniere'); setShowUserDropdown(false); }}>
                            <span className="dd-turnier-manage">→ Alle verwalten</span>
                          </div>
                        </div>
                        <div className="dropdown-divider" />
                      </>
                    );
                  })()}

                  {/* ── Events & Camps ───────────────────────────── */}
                  {events.length > 0 && (() => {
                    const today = new Date(); today.setHours(0,0,0,0);
                    const upcoming = events.filter(e => new Date(e.datum) >= today).sort((a,b) => new Date(a.datum)-new Date(b.datum));
                    const past     = events.filter(e => new Date(e.datum) < today).sort((a,b) => new Date(b.datum)-new Date(a.datum));
                    const fmtE = (d) => new Date(d).toLocaleDateString('de-DE', { day:'2-digit', month:'short', year:'numeric' });
                    const renderE = (e) => (
                      <div key={e.event_id} className="dd-turnier-item"
                        onClick={() => { changeView('events'); setShowUserDropdown(false); }}>
                        <span className="dd-turnier-name">{e.typ === 'Camp' ? '🏕️ ' : ''}{e.name}</span>
                        <span className="dd-turnier-date">{fmtE(e.datum)}</span>
                      </div>
                    );
                    return (
                      <>
                        <div className="dropdown-section">
                          <div className="dropdown-label">合宿 Events &amp; Camps</div>
                          <div className="dd-turnier-list">
                            {upcoming.map(renderE)}
                            {past.length > 0 && upcoming.length > 0 && <div className="dd-turnier-divider">Vergangene</div>}
                            {past.map(renderE)}
                          </div>
                          <div className="dropdown-item" onClick={() => { changeView('events'); setShowUserDropdown(false); }}>
                            <span className="dd-turnier-manage">→ Alle verwalten</span>
                          </div>
                        </div>
                        <div className="dropdown-divider" />
                      </>
                    );
                  })()}

                  <div className="dropdown-section">
                    <div className="dropdown-item" onClick={() => { changeView('overview'); setShowUserDropdown(false); }}>
                      <span>{t('admin.nav.overview')}</span>
                    </div>
                    <div className="dropdown-item" onClick={() => { navigate('/dashboard/ankuendigungen'); setShowUserDropdown(false); setUngeleseneAnnouncements(0); }}>
                      <span>{t('admin.nav.announcements')}</span>
                      {ungeleseneAnnouncements > 0 && (
                        <span style={{ marginLeft:'auto', background:'#ef4444', color:'#fff', borderRadius:'10px', padding:'0.1rem 0.45rem', fontSize:'0.7rem', fontWeight:700 }}>
                          {ungeleseneAnnouncements}
                        </span>
                      )}
                    </div>
                  </div>

                  {user?.isAdmin && (<>
                    <div className="dropdown-divider"></div>
                    <div className="dropdown-section">
                      <div className="dropdown-label">System</div>
                      <div className="dropdown-item" onClick={() => { changeView('system-info'); setShowUserDropdown(false); }}>
                        <span>⚙️ System & Info</span>
                      </div>
                      <div className="dropdown-item" onClick={() => { changeView('audit'); setShowUserDropdown(false); }}>
                        <span>📋 Audit-Log</span>
                      </div>
                      {/* Aufgaben 171/174: Push-Nachrichten aufs Gerät */}
                      <PushSchalter />
                    </div>
                  </>)}

                  <div className="dropdown-divider"></div>

                  <div className="dropdown-section">
                    <div className="dropdown-item" onClick={() => { setShowUserDropdown(false); navigate('/sicherheit'); }}>
                      <span>🛡️ Konto & Sicherheit</span>
                    </div>
                    <div className="dropdown-item logout" onClick={handleLogout}>
                      <span>{t('admin.nav.logout')}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Zwei-Faktor fehlt: Stand 26.09.2026 hatte kein einziges Admin-Konto
          ihn eingerichtet. Ab ZWEI_FAKTOR_PFLICHT_ADMIN_AB (Backend-.env)
          ist er Pflicht — der Hinweis nennt dann das Datum. */}
      {zweiFaktor && zweiFaktor.moeglich && !zweiFaktor.aktiv && !zweiFaktor.extern && (
        <div className="md-zwei-faktor-hinweis" role="status">
          <span>
            <strong>Zwei-Faktor-Anmeldung ist für dieses Konto nicht eingerichtet.</strong>{' '}
            {zweiFaktor.pflicht_ab
              ? `Ab ${zweiFaktor.pflicht_ab.split('-').reverse().join('.')} ist sie für Admin-Konten Pflicht.`
              : 'An einem Admin-Konto hängen Turniere, Anmeldungen und Personendaten — ein Passwort allein schützt das schlecht.'}
          </span>
          <button type="button" onClick={() => navigate('/sicherheit')}>Jetzt einrichten</button>
        </div>
      )}

      {/* Main Content */}
      <React.Suspense fallback={<div className="lazy-loading-spinner">Laden…</div>}>
      <div className="dashboard-content">
        {activeView === 'overview' && (
          <DashboardOverview user={user} setActiveView={changeView} activeView={activeView} onLogout={handleLogout} selectedTurnier={selectedTurnier} turniere={turniere} setSelectedTurnier={setSelectedTurnier} openFormenWindow={openFormenWindow} setStaffTab={setStaffTab} />
        )}

        {activeView === 'wettkaempfer-registrieren' && (
          <div className="dashboard-module">
            <WettkaempferRegistrieren />
          </div>
        )}

        {activeView === 'wettkaempfer' && (
          <div className="dashboard-module">
            <WettkaempferDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'wettkaempfer-alle' && (
          <div className="dashboard-module">
            <WettkaempferDashboard selectedTurnier={null} />
          </div>
        )}

        {activeView === 'turniere' && (
          <div className="dashboard-module">
            <TurnierDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView.startsWith('turnier-bearbeiten') && (
          <div className="dashboard-module full-width">
            <TurnierBearbeitenPage />
          </div>
        )}

        {activeView === 'brackets' && (
          <div className="dashboard-module dashboard-module--no-bg">
            <BracketDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'formen' && (
          <div className="dashboard-module">
            <FormenDashboard turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id} />
          </div>
        )}

        {activeView === 'selbstverteidigung' && (
          <div className="dashboard-module">
            <SelbstverteidigungDashboard turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id} />
          </div>
        )}

        {activeView === 'matten-wettkampf' && (
          <div className="dashboard-module">
            <MattenWettkampfDashboard
              turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id}
              setActiveView={changeView}
            />
          </div>
        )}

        {activeView === 'anmeldungen' && (
          <div className="dashboard-module">
            <AnmeldeDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'einzelstarter' && (
          <div className="dashboard-module">
            <EinzelstarterDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'vereinsmails' && (
          <div className="dashboard-module">
            <VereinsmailsDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'einwaage' && (
          <div className="dashboard-module full-width">
            <EinwaageDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'checkin' && (
          <div className="dashboard-module full-width">
            <CheckinDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'modus-uebersicht' && (
          <div className="dashboard-module full-width">
            <ModusUebersichtDashboard
              initialTab={modusNavState?.initialTab}
              initialModus={modusNavState?.initialModus}
              turnierId={modusNavState?.turnierId}
              kategorieId={modusNavState?.kategorieId}
            />
          </div>
        )}

        {activeView === 'ankuendigungen' && (
          <div className="dashboard-module full-width">
            <AkuendigungenDashboard isAdmin={!!user?.isAdmin} selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'akkreditierung' && (
          <div className="dashboard-module full-width">
            <AkkreditierungDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}


        {activeView === 'druck' && (
          <div className="dashboard-module full-width">
            <DruckDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'events' && (
          <div className="dashboard-module">
            <EventsDashboard />
          </div>
        )}

        {activeView === 'streaming' && (
          <div className="dashboard-module">
            <StreamingDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'analytics' && (
          <div className="dashboard-module">
            <AnalyticsDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'divisionen' && (
          <div className="dashboard-module">
            <DivisionDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {(activeView === 'vereine' || activeView.startsWith('vereine/')) && (
          <div className="dashboard-module">
            {(() => {
              const path = location.pathname;

              // Check for /dashboard/vereine/neu first (before numeric check)
              if (path === '/dashboard/vereine/neu' || activeView === 'vereine/neu') {
                return <VereinCreatePage />;
              }

              // Extract vereinId from activeView if it's a detail view
              if (activeView.startsWith('vereine/')) {
                const vereinId = activeView.split('/')[1];
                return <VereinDetailPage vereinId={vereinId} />;
              }

              return <VereineListePage />;
            })()}
          </div>
        )}

        {activeView === 'bruchtest' && (
          <div className="dashboard-module full-width">
            <BruchtestDashboard turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id} />
          </div>
        )}

        {activeView === 'zeitplan' && (
          <div className="dashboard-module full-width">
            <ZeitplanDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'ergebnisse' && (
          <div className="dashboard-module full-width">
            {/* Aufgabe 200: eine Seite für Siegerehrung, Planung und Gesamtwertung */}
            <ErgebnisDashboard selectedTurnier={selectedTurnier} startTab="gesamtwertung" />
          </div>
        )}

        {activeView === 'urkunden' && (
          <div className="dashboard-module">
            <div className="coming-soon">
              <h2>🏅 {t('admin.modules.certificates')}</h2>
              <p>{t('admin.common.coming_soon')}</p>
              <div className="feature-preview">
                <h3>{t('admin.common.planned_features')}</h3>
                <ul>
                  <li>Sieger-Urkunden generieren</li>
                  <li>Platzierungs-Urkunden</li>
                  <li>Teilnahme-Zertifikate</li>
                  <li>PDF-Export und Druck</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeView === 'live-ergebnisse' && (
          <div className="dashboard-module full-width">
            <LiveSpectatorDashboard turnierId={selectedTurnier?.turnier_id} />
          </div>
        )}

        {activeView === 'turnier-einstellungen' && (
          <TurnierEinstellungen />
        )}

        {(activeView === 'staff-management' || activeView === 'regstaff' || activeView === 'kampfrichter-manager') && (
          <div className="dashboard-module full-width">
            <nav className="sm-page-nav">
              {[
                { key: 'personal',     icon: '👥', label: 'Personal' },
                { key: 'reghelfer',    icon: '⚡', label: 'Registrierungshelfer' },
                { key: 'kampfrichter', icon: '🥋', label: 'Kampfrichter' },
                { key: 'einteilung',   icon: '🗂',  label: 'Einteilung' },
                { key: 'einwaage',     icon: '⚖',  label: 'Einwaage-Helfer' },
              ].map(tab => (
                <button
                  key={tab.key}
                  className={`sm-page-tab${staffTab === tab.key ? ' sm-page-tab--active' : ''}`}
                  onClick={() => setStaffTab(tab.key)}
                >
                  <span className="sm-page-tab-icon">{tab.icon}</span>
                  <span className="sm-page-tab-label">{tab.label}</span>
                </button>
              ))}
            </nav>
            {staffTab === 'personal'     && <StaffManager turnierId={selectedTurnier?.turnier_id} turnier={selectedTurnier} />}
            {staffTab === 'reghelfer'    && <RegStaffManager turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id} turnier={selectedTurnier} />}
            {staffTab === 'kampfrichter' && <KampfrichterManager turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id} turnier={selectedTurnier} />}
            {staffTab === 'einteilung'   && <RefereeManager turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id} />}
            {staffTab === 'einwaage'     && <WeighStaffManager turnierId={selectedTurnier?.turnier_id || selectedTurnier?.id} turnier={selectedTurnier} />}
          </div>
        )}

        {activeView === 'siegerehrung' && (
          <div className="dashboard-module full-width">
            <ErgebnisDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'saisonrangliste' && (
          <div className="dashboard-module full-width">
            <SaisonRanglisteDashboard />
          </div>
        )}

        {BERICHT_VIEW_MAP[activeView] !== undefined && (
          <div className="dashboard-module full-width" style={{ height: '100%', overflow: 'hidden' }}>
            <BerichteDashboard selectedTurnier={selectedTurnier} initialReport={BERICHT_VIEW_MAP[activeView]} />
          </div>
        )}

        {activeView === 'kampfflaechen' && (
          <div className="dashboard-module full-width">
            <KampfflaechenManager turnierId={selectedTurnier?.turnier_id} />
          </div>
        )}

        {activeView === 'platzierung' && (
          <div className="dashboard-module full-width">
            <PlatzierungDashboard selectedTurnier={selectedTurnier} />
          </div>
        )}

        {activeView === 'sports' && user?.isAdmin && (
          <div className="dashboard-module">
            <SportsManager />
          </div>
        )}

        {activeView === 'admin' && user?.isAdmin && (
          <div className="dashboard-module">
            <AdminPanel user={user} />
          </div>
        )}

        {activeView === 'system-info' && user?.isAdmin && (
          <div className="dashboard-module">
            <SystemInfoPanel version={CURRENT_VERSION} buildDate={CURRENT_BUILD_DATE} changelog={CHANGELOG} />
          </div>
        )}

        {activeView === 'audit' && user?.isAdmin && (
          <div className="dashboard-module">
            <AuditPanel />
          </div>
        )}

        {activeView === 'verbaende' && user?.verband_id_tenant === null && (
          <div className="dashboard-module full-width">
            <VerbaendeDashboard />
          </div>
        )}

      </div>
      </React.Suspense>

      {/* Hilfe-Modal */}
      {showHelp && (
        <div className="help-modal-overlay" onClick={() => setShowHelp(false)}>
          <div className="help-modal" onClick={e => e.stopPropagation()}>
            <div className="help-modal-header">
              <div className="help-modal-title">
                <span>Hilfe & Info</span>
                <span className="help-version-badge">v{CURRENT_VERSION}</span>
              </div>
              <button className="help-modal-close" onClick={() => setShowHelp(false)}>✕</button>
            </div>

            {/* Tabs */}
            <div className="help-modal-tabs">
              <button
                className={`help-tab ${helpTab === 'hilfe' ? 'active' : ''}`}
                onClick={() => setHelpTab('hilfe')}
              >Hilfe</button>
              <button
                className={`help-tab ${helpTab === 'changelog' ? 'active' : ''}`}
                onClick={() => {
                  setHelpTab('changelog');
                  localStorage.setItem(LS_VERSION_KEY, CURRENT_VERSION);
                  setHasNewVersion(false);
                }}
              >
                Changelog
                {hasNewVersion && helpTab !== 'changelog' && <span className="help-tab-dot" />}
              </button>
            </div>

            <div className="help-modal-body">
              {helpTab === 'hilfe' && (<>
                <div className="help-section">
                  <h4>Organisation</h4>
                  <div className="help-items">
                    <div className="help-item"><span className="help-icon">🏆</span><div><strong>Turnier-Einstellungen</strong><p>Name, Datum, Ort und allgemeine Turnierdaten verwalten.</p></div></div>
                    <div className="help-item"><span className="help-icon">🗂️</span><div><strong>Divisionen</strong><p>Gewichtsklassen und Altersgruppen für das Turnier konfigurieren.</p></div></div>
                    <div className="help-item"><span className="help-icon">🥋</span><div><strong>Kampfflächen</strong><p>Matten und Bereiche anlegen, Nummern und Namen vergeben.</p></div></div>
                    <div className="help-item"><span className="help-icon">👥</span><div><strong>Staff</strong><p>Kampfrichter, Helfer und weiteres Personal verwalten und Turnieren zuweisen. Einsatzplan für alle Matten.</p></div></div>
                    <div className="help-item"><span className="help-icon">📅</span><div><strong>Zeitplan</strong><p>Ablaufplan und Zeitfenster für das Turnier festlegen.</p></div></div>
                  </div>
                </div>
                <div className="help-section">
                  <h4>Wettkampf</h4>
                  <div className="help-items">
                    <div className="help-item"><span className="help-icon">📝</span><div><strong>Anmeldungen</strong><p>Vereinsanmeldungen einsehen, bestätigen oder stornieren.</p></div></div>
                    <div className="help-item"><span className="help-icon">🥊</span><div><strong>Wettkämpfer</strong><p>Alle Teilnehmer des Turniers, Filter nach Gewicht, Alter, Level.</p></div></div>
                    <div className="help-item"><span className="help-icon">🌿</span><div><strong>Formen</strong><p>Formen-Kategorien und Teilnehmer verwalten. Schreiber-Fenster starten.</p></div></div>
                    <div className="help-item"><span className="help-icon">🗺️</span><div><strong>Mattenfläche</strong><p>Übersicht aller Matten mit zugeordneten Formen-Kategorien und Schreiber-Links.</p></div></div>
                    <div className="help-item"><span className="help-icon">🎯</span><div><strong>Brackets</strong><p>Auslosungen und K.O.-Runden erstellen und verwalten.</p></div></div>
                  </div>
                </div>
                <div className="help-section">
                  <h4>Turniertag</h4>
                  <div className="help-items">
                    <div className="help-item"><span className="help-icon">✅</span><div><strong>Check-in</strong><p>Wettkämpfer und Personal beim Turniertag einchecken.</p></div></div>
                    <div className="help-item"><span className="help-icon">⚖️</span><div><strong>Einwaage</strong><p>Gewichtskontrolle durchführen und dokumentieren.</p></div></div>
                    <div className="help-item"><span className="help-icon">🏅</span><div><strong>Ergebnisse</strong><p>Kampfergebnisse eingeben und Ranglisten berechnen.</p></div></div>
                    <div className="help-item"><span className="help-icon">🎥</span><div><strong>Streaming</strong><p>Live-Stream-Kanäle verwalten und Übertragungen starten.</p></div></div>
                  </div>
                </div>
                <div className="help-section">
                  <h4>Berichte & Druck</h4>
                  <div className="help-items">
                    <div className="help-item"><span className="help-icon">📊</span><div><strong>Berichte</strong><p>Statistiken, Teilnehmerlisten, Ergebnisberichte und mehr exportieren.</p></div></div>
                    <div className="help-item"><span className="help-icon">🖨️</span><div><strong>Druck</strong><p>Startlisten, Urkunden, Akkreditierungskarten und Etiketten drucken.</p></div></div>
                  </div>
                </div>
              </>)}

              {helpTab === 'changelog' && (
                <div className="changelog-list">
                  {CHANGELOG.map((entry, i) => {
                    const typeConfig = {
                      major:       { label: 'Major Update', color: '#e67e22' },
                      feature:     { label: 'Neue Funktion', color: '#2ecc71' },
                      improvement: { label: 'Verbesserung', color: '#3498db' },
                      bugfix:      { label: 'Bugfix', color: '#e74c3c' },
                      security:    { label: 'Sicherheit', color: '#9b59b6' },
                    }[entry.type] || { label: entry.type, color: '#888' };
                    return (
                      <div key={i} className={`cl-entry ${i === 0 ? 'cl-entry--latest' : ''}`}>
                        <div className="cl-entry-header">
                          <span className="cl-version">v{entry.version}</span>
                          <span className="cl-type-badge" style={{ background: typeConfig.color }}>{typeConfig.label}</span>
                          <span className="cl-date">{entry.date}</span>
                        </div>
                        <div className="cl-title">{entry.title}</div>
                        <ul className="cl-highlights">
                          {entry.highlights.map((h, j) => <li key={j}>{h}</li>)}
                        </ul>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            <div className="help-modal-footer">
              TDA Tournament Software · v{CURRENT_VERSION} · Build {CURRENT_BUILD_DATE} · © 2024–2026 TDA International
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Admin Panel Component — Benutzerverwaltung
function AdminPanel({ user }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // null | { type: 'create'|'edit'|'password'|'delete', user? }
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const token = localStorage.getItem('token') || localStorage.getItem('authToken');
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/auth/users', { headers });
      const data = await res.json();
      if (data.success) setUsers(data.data);
      else setError(data.error);
    } catch (e) {
      setError('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const openCreate = () => { setForm({ name: '', benutzername: '', email: '', passwort: '', rolle: 'verein' }); setModal({ type: 'create' }); setMsg(null); };
  const openEdit = (u) => { setForm({ name: u.name, benutzername: u.benutzername, email: u.email || '', rolle: u.rolle }); setModal({ type: 'edit', user: u }); setMsg(null); };
  const openPassword = (u) => { setForm({ passwort: '' }); setModal({ type: 'password', user: u }); setMsg(null); };
  const openDelete = (u) => { setModal({ type: 'delete', user: u }); setMsg(null); };
  const closeModal = () => { setModal(null); setMsg(null); };

  const handleSave = async () => {
    setSaving(true); setMsg(null);
    try {
      let res, url, method, body;
      if (modal.type === 'create') {
        url = '/api/auth/users'; method = 'POST';
        body = { name: form.name, benutzername: form.benutzername, email: form.email, passwort: form.passwort, rolle: form.rolle };
      } else if (modal.type === 'edit') {
        url = `/api/auth/users/${modal.user.vereins_id}`; method = 'PUT';
        body = { name: form.name, benutzername: form.benutzername, email: form.email, rolle: form.rolle };
      } else if (modal.type === 'password') {
        url = `/api/auth/users/${modal.user.vereins_id}/password`; method = 'PUT';
        body = { passwort: form.passwort };
      } else if (modal.type === 'delete') {
        url = `/api/auth/users/${modal.user.vereins_id}`; method = 'DELETE'; body = {};
      }
      res = await fetch(url, { method, headers, body: JSON.stringify(body) });
      const data = await res.json();
      if (data.success) {
        setMsg({ ok: true, text: data.message });
        await loadUsers();
        if (modal.type !== 'edit' && modal.type !== 'password') setTimeout(closeModal, 800);
      } else {
        setMsg({ ok: false, text: data.error });
      }
    } catch (e) {
      setMsg({ ok: false, text: 'Verbindungsfehler' });
    } finally {
      setSaving(false);
    }
  };

  const rolleLabel = (r) => ({ admin: 'Admin', verein: 'Verein', kampfrichter: 'Kampfrichter' }[r] || r);
  const rolleBadge = (r) => ({ admin: '#e74c3c', verein: '#3498db', kampfrichter: '#27ae60' }[r] || '#888');

  return (
    <div className="admin-panel">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0 }}>👥 Benutzerverwaltung</h2>
        <button className="btn-primary" onClick={openCreate} style={{ padding: '8px 18px' }}>+ Benutzer anlegen</button>
      </div>

      {loading && <p style={{ color: 'var(--text-muted)' }}>Lade Benutzer…</p>}
      {error && <p style={{ color: '#e74c3c' }}>{error}</p>}

      {!loading && !error && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
              {['Name', 'Benutzername', 'E-Mail', 'Rolle', 'Letzter Login', 'Aktionen'].map(h => (
                <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text-muted)', fontWeight: 600 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(u => (
              <tr key={u.vereins_id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                <td style={{ padding: '10px' }}>{u.name}</td>
                <td style={{ padding: '10px', fontFamily: 'monospace' }}>{u.benutzername}</td>
                <td style={{ padding: '10px', color: 'var(--text-muted)', fontSize: 12 }}>{u.email}</td>
                <td style={{ padding: '10px' }}>
                  <span style={{ background: rolleBadge(u.rolle), color: '#fff', borderRadius: 4, padding: '2px 8px', fontSize: 11 }}>
                    {rolleLabel(u.rolle)}
                  </span>
                </td>
                <td style={{ padding: '10px', color: 'var(--text-muted)', fontSize: 12 }}>
                  {u.letzter_login ? new Date(u.letzter_login).toLocaleDateString('de-DE') : '—'}
                </td>
                <td style={{ padding: '10px' }}>
                  <button onClick={() => openEdit(u)} style={{ marginRight: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>Bearbeiten</button>
                  <button onClick={() => openPassword(u)} style={{ marginRight: 6, padding: '4px 10px', fontSize: 12, cursor: 'pointer' }}>Passwort</button>
                  {u.vereins_id !== user?.vereins_id && (
                    <button onClick={() => openDelete(u)} style={{ padding: '4px 10px', fontSize: 12, cursor: 'pointer', background: '#e74c3c', color: '#fff', border: 'none', borderRadius: 4 }}>Löschen</button>
                  )}
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 20, color: 'var(--text-muted)', textAlign: 'center' }}>Keine Benutzer vorhanden</td></tr>
            )}
          </tbody>
        </table>
      )}

      {modal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000 }}>
          <div style={{ background: 'rgba(22,22,34,0.99)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: 28, minWidth: 380, maxWidth: 480, width: '100%' }}>
            <h3 style={{ marginTop: 0 }}>
              {modal.type === 'create' && 'Neuen Benutzer anlegen'}
              {modal.type === 'edit' && `Benutzer bearbeiten: ${modal.user.name}`}
              {modal.type === 'password' && `Passwort ändern: ${modal.user.name}`}
              {modal.type === 'delete' && `Benutzer löschen?`}
            </h3>

            {modal.type === 'delete' ? (
              <p>Soll <strong>{modal.user.name}</strong> ({modal.user.benutzername}) wirklich gelöscht werden?</p>
            ) : modal.type === 'password' ? (
              <div>
                <label style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>Neues Passwort</label>
                <input type="password" value={form.passwort} onChange={e => setForm(f => ({ ...f, passwort: e.target.value }))}
                  style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', boxSizing: 'border-box' }} autoFocus />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {[
                  { label: 'Name', key: 'name', type: 'text' },
                  { label: 'Benutzername', key: 'benutzername', type: 'text' },
                  { label: 'E-Mail (optional)', key: 'email', type: 'email' },
                  ...(modal.type === 'create' ? [{ label: 'Passwort', key: 'passwort', type: 'password' }] : []),
                ].map(({ label, key, type }) => (
                  <div key={key}>
                    <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>{label}</label>
                    <input type={type} value={form[key] || ''} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
                      style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)', boxSizing: 'border-box' }} />
                  </div>
                ))}
                <div>
                  <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Rolle</label>
                  <select value={form.rolle || 'verein'} onChange={e => setForm(f => ({ ...f, rolle: e.target.value }))}
                    style={{ width: '100%', padding: 8, borderRadius: 6, border: '1px solid var(--border-color)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}>
                    <option value="admin">Admin</option>
                    <option value="verein">Verein</option>
                    <option value="kampfrichter">Kampfrichter</option>
                  </select>
                </div>
              </div>
            )}

            {msg && (
              <p style={{ color: msg.ok ? '#27ae60' : '#e74c3c', marginTop: 12, fontSize: 13 }}>{msg.text}</p>
            )}

            <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
              <button onClick={closeModal} style={{ padding: '8px 16px', borderRadius: 6, cursor: 'pointer' }}>Abbrechen</button>
              <button onClick={handleSave} disabled={saving}
                style={{ padding: '8px 18px', borderRadius: 6, background: modal.type === 'delete' ? '#e74c3c' : 'var(--accent-primary)', color: '#fff', border: 'none', cursor: 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? 'Speichere…' : modal.type === 'delete' ? 'Ja, löschen' : 'Speichern'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Dashboard Overview Component
function DashboardOverview({ user, setActiveView, activeView, onLogout, selectedTurnier, turniere, setSelectedTurnier, openFormenWindow, setStaffTab }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [modulesTab, setModulesTab] = useState(
    () => localStorage.getItem('tda-dashboard-tab') || 'organisation'
  );
  const [showWettkampftag, setShowWettkampftag] = useState(false);
  const [showSchnellreg, setShowSchnellreg] = useState(false);
  const [stats, setStats] = useState({
    turniere: 0,
    wettkaempfer: 0,
    anmeldungen: 0,
    personal: 0,
    kategorien: 0
  });
  const [, setRecentActivity] = useState([]);
  const [briefing, setBriefing] = useState(null); // null = nicht geladen, [] = keine Punkte, [...] = Punkte
  const [showBriefing, setShowBriefing] = useState(false);

  // Primitive ID als stabile Dep-Referenz (verhindert Object-Ref-Wechsel-Trigger)
  const selectedTurnierId = selectedTurnier?.turnier_id ?? selectedTurnier?.id ?? null;

  const fetchDashboardStats = useCallback(async () => {
    if (!selectedTurnierId) {
      setStats({ turniere: 0, wettkaempfer: 0, anmeldungen: 0, personal: 0, activeStreams: 0 });
      setRecentActivity([]);
      return;
    }

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    const briefingKey = `tda-briefing-${new Date().toISOString().slice(0,10)}`;

    setStats(prev => ({ ...prev, turniere: 1 }));

    // Alle Requests parallel starten
    const [wkRes, anmRes, persRes, actRes, briefRes, divRes] = await Promise.allSettled([
      fetch(`/api/wettkaempfer?turnierId=${selectedTurnierId}`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`/api/anmeldungen/stats?turnierId=${selectedTurnierId}`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`/api/personal/turnier/${selectedTurnierId}`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`/api/dashboard/recent-activity?turnierId=${selectedTurnierId}`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`/api/dashboard/briefing?turnierId=${selectedTurnierId}`, { headers }).then(r => r.ok ? r.json() : null),
      fetch(`/api/divisionen?turnierId=${selectedTurnierId}`, { headers }).then(r => r.ok ? r.json() : null),
    ]);

    // Wettkämpfer
    if (wkRes.status === 'fulfilled' && wkRes.value) {
      const d = wkRes.value;
      const count = d.success && d.data ? d.data.length : Array.isArray(d) ? d.length : 0;
      setStats(prev => ({ ...prev, wettkaempfer: count }));
    }

    // Anmeldungen
    if (anmRes.status === 'fulfilled' && anmRes.value) {
      setStats(prev => ({ ...prev, anmeldungen: anmRes.value.data?.gesamt?.gesamt_anmeldungen || 0 }));
    }

    // Personal
    if (persRes.status === 'fulfilled' && persRes.value) {
      setStats(prev => ({ ...prev, personal: persRes.value.data?.length || 0 }));
    }

    // Letzte Aktivitäten
    if (actRes.status === 'fulfilled' && actRes.value) {
      setRecentActivity(actRes.value.data || []);
    }

    // Kategorien/Divisionen
    if (divRes.status === 'fulfilled' && divRes.value) {
      const d = divRes.value;
      const count = d.success && d.data ? d.data.length : Array.isArray(d) ? d.length : 0;
      setStats(prev => ({ ...prev, kategorien: count }));
    }

    // Tagesbriefing
    if (briefRes.status === 'fulfilled' && briefRes.value) {
      const briefData = briefRes.value.data || [];
      setBriefing(briefData);
      if (!localStorage.getItem(briefingKey)) {
        if (briefData.some(i => !i.done)) setShowBriefing(true);
      }
    }
  }, [selectedTurnierId]); // primitive dep → kein Re-Trigger bei gleicher turnier_id mit neuer Objekt-Referenz

  useEffect(() => {
    fetchDashboardStats();
  }, [fetchDashboardStats]);

  return (
    <div className="dashboard-overview">
      {/* Kombinierter Container: Statistik-Karten */}
      {turniere.length > 0 && (
        <div className="turnier-stats-combined">
          {/* Kompakte Statistik-Karten */}
          <div className="stats-grid-compact">
            <div key="stat-turniere" className="stat-card-compact">
              <div className="stat-icon-compact">🏆</div>
              <div className="stat-content-compact">
                <h3>{stats.turniere}</h3>
                <p>{t('admin.overview.stat_tournaments')}</p>
              </div>
            </div>

            <div key="stat-wettkaempfer" className="stat-card-compact">
              <div className="stat-icon-compact">🥊</div>
              <div className="stat-content-compact">
                <h3>{stats.wettkaempfer}</h3>
                <p>{t('admin.overview.stat_athletes')}</p>
              </div>
            </div>

            <div key="stat-anmeldungen" className="stat-card-compact">
              <div className="stat-icon-compact">📝</div>
              <div className="stat-content-compact">
                <h3>{stats.anmeldungen}</h3>
                <p>{t('admin.overview.stat_registrations')}</p>
              </div>
            </div>

            <div key="stat-personal" className="stat-card-compact">
              <div className="stat-icon-compact">👥</div>
              <div className="stat-content-compact">
                <h3>{stats.personal}</h3>
                <p>Personal</p>
              </div>
            </div>

            <div key="stat-kategorien" className="stat-card-compact">
              <div className="stat-icon-compact">🗂️</div>
              <div className="stat-content-compact">
                <h3>{stats.kategorien}</h3>
                <p>Kategorien</p>
              </div>
            </div>

            {/* Countdown zum nächsten Turnier */}
            {(() => {
              const today = new Date(); today.setHours(0,0,0,0);
              const next = turniere
                .map(t => ({ ...t, d: new Date(t.datum) }))
                .filter(t => t.d >= today)
                .sort((a, b) => a.d - b.d)[0];
              if (!next) return null;
              const days = Math.round((next.d - today) / 86400000);
              return (
                <div key="stat-countdown" className="stat-card-compact stat-card-countdown">
                  <div className="stat-icon-compact">📅</div>
                  <div className="stat-content-compact">
                    <h3>{days === 0 ? 'Heute!' : `${days} Tage`}</h3>
                    <p className="stat-countdown-name" title={next.name}>{next.name}</p>
                  </div>
                </div>
              );
            })()}

            {/* Wettkampftag Button in Stats-Grid */}
            <div key="stat-wettkampftag" className="stat-card-compact stat-card-wettkampftag" onClick={() => setShowWettkampftag(true)}>
              <div className="stat-icon-compact">🏅</div>
              <div className="stat-content-compact">
                <h3>Wettkampftag</h3>
                <p>Vorbereitung</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <WettkampftagChecklist
        isOpen={showWettkampftag}
        onClose={() => setShowWettkampftag(false)}
        selectedTurnier={selectedTurnier}
        setActiveView={setActiveView}
      />

      {showSchnellreg && (
        <React.Suspense fallback={<div className="lazy-loading-spinner">Laden…</div>}>
          <SchnellregistrierungModal
            turnier={selectedTurnier}
            onClose={() => setShowSchnellreg(false)}
          />
        </React.Suspense>
      )}

      {/* Aktives Turnier Banner */}
      {selectedTurnier && (() => {
        const today = new Date(); today.setHours(0,0,0,0);
        const datum = new Date(selectedTurnier.datum); datum.setHours(0,0,0,0);
        const diff = Math.round((datum - today) / 86400000);
        let phase, phaseColor;
        if (diff === 0) { phase = 'Wettkampftag'; phaseColor = '#ff4444'; }
        else if (diff < 0) { phase = 'Abgeschlossen'; phaseColor = '#666'; }
        else if (selectedTurnier.anmeldeschluss && today <= new Date(selectedTurnier.anmeldeschluss)) {
          phase = 'Anmeldung läuft'; phaseColor = '#4ade80';
        } else if (selectedTurnier.anmeldeschluss && today > new Date(selectedTurnier.anmeldeschluss)) {
          phase = 'Anmeldeschluss abgelaufen'; phaseColor = '#fbbf24';
        } else {
          phase = 'Vorbereitung'; phaseColor = '#d4af37';
        }
        const datumStr = selectedTurnier.datum
          ? new Date(selectedTurnier.datum).toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })
          : (selectedTurnier.datum_text || 'Termin offen');
        return (
          <div
            className="active-turnier-banner active-turnier-banner--clickable"
            onClick={() => { setModulesTab('wettkampftag'); localStorage.setItem('tda-dashboard-tab', 'wettkampftag'); }}
            title="Zu Wettkampftag"
          >
            <div className="active-turnier-banner__phase" style={{ color: phaseColor }}>
              <span className="active-turnier-banner__phase-dot" style={{ background: phaseColor }} />
              {phase}
            </div>
            <div className="active-turnier-banner__name">{selectedTurnier.name}</div>
            <div className="active-turnier-banner__meta">
              <span>📅 {datumStr}</span>
              {selectedTurnier.ort && <span>📍 {selectedTurnier.ort}</span>}
              {diff > 0 && <span>⏳ noch {diff} Tag{diff !== 1 ? 'e' : ''}</span>}
              {diff === 0 && <span style={{ color: '#ff4444', fontWeight: 600 }}>🏆 Heute!</span>}
            </div>
          </div>
        );
      })()}

      {/* Dashboard Modules */}
      <div className="dashboard-modules">
        <div className="modules-container">
          {/* Tabs Navigation */}
          <div className="dashboard-tabs">
            <button className={`dashboard-tab dashboard-tab--img ${modulesTab === 'organisation' ? 'active' : ''}`} onClick={() => { setModulesTab('organisation'); localStorage.setItem('tda-dashboard-tab', 'organisation'); }}>
              <img src={`${process.env.PUBLIC_URL}/tab-organisation.webp?v=4`} alt="Organisation" />
              <div className="tab-img-label"><span>Organisation</span></div>
            </button>
            <button className={`dashboard-tab dashboard-tab--img ${modulesTab === 'wettkampftag' ? 'active' : ''}`} onClick={() => { setModulesTab('wettkampftag'); localStorage.setItem('tda-dashboard-tab', 'wettkampftag'); }}>
              <img src={`${process.env.PUBLIC_URL}/tab-wettkampftag.webp?v=4`} alt="Wettkampftag" />
              <div className="tab-img-label"><span>Wettkampftag</span></div>
            </button>
            <button className={`dashboard-tab dashboard-tab--img ${modulesTab === 'wettkampf' ? 'active' : ''}`} onClick={() => { setModulesTab('wettkampf'); localStorage.setItem('tda-dashboard-tab', 'wettkampf'); }}>
              <img src={`${process.env.PUBLIC_URL}/tab-wettkampf.webp?v=4`} alt="Wettkampf" />
              <div className="tab-img-label"><span>Wettkampf</span></div>
            </button>
            <button className={`dashboard-tab dashboard-tab--img ${modulesTab === 'ergebnisse' ? 'active' : ''}`} onClick={() => { setModulesTab('ergebnisse'); localStorage.setItem('tda-dashboard-tab', 'ergebnisse'); }}>
              <img src={`${process.env.PUBLIC_URL}/tab-ergebnisse.webp?v=4`} alt="Ergebnisse" />
              <div className="tab-img-label"><span>Ergebnisse</span></div>
            </button>
            <button className={`dashboard-tab dashboard-tab--img ${modulesTab === 'berichte' ? 'active' : ''}`} onClick={() => { setModulesTab('berichte'); localStorage.setItem('tda-dashboard-tab', 'berichte'); }}>
              <img src={`${process.env.PUBLIC_URL}/tab-berichte.webp?v=4`} alt="Berichte" />
              <div className="tab-img-label"><span>Berichte</span></div>
            </button>
          </div>

          {/* Organisation Tab */}
          {modulesTab === 'organisation' && (
            <div className="modules-grid">
              <div className="module-card" onClick={() => setActiveView('turniere')}>
                <div className="module-icon">大会</div>
                <div className="module-content">
                  <h4>{t('admin.modules.tournaments')}</h4>
                  <p>{t('admin.modules.tournaments_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('vereine')}>
                <div className="module-icon">道場</div>
                <div className="module-content">
                  <h4>{t('admin.modules.clubs')}</h4>
                  <p>{t('admin.modules.clubs_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('divisionen')} title="Division = eine ausgeschriebene Startklasse, z. B. „Pointfighting Herren −75 kg, Fortgeschrittene“. Wer sich anmeldet, wählt eine Division.">
                <div className="module-icon">部門</div>
                <div className="module-content">
                  <h4>{t('admin.modules.divisions')}</h4>
                  <p>{t('admin.modules.divisions_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('kampfflaechen')} title="Kampffläche = Matte bzw. Ring in der Halle. Jede Division wird einer Fläche zugeteilt.">
                <div className="module-icon">畳</div>
                <div className="module-content">
                  <h4>{t('admin.modules.mats')}</h4>
                  <p>{t('admin.modules.mats_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('turnier-einstellungen')}>
                <div className="module-icon">設定</div>
                <div className="module-content">
                  <h4>{t('admin.modules.tournament_settings')}</h4>
                  <p>{t('admin.modules.tournament_settings_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('staff-management')}>
                <div className="module-icon">役員</div>
                <div className="module-content">
                  <h4>{t('admin.modules.staff')}</h4>
                  <p>{t('admin.modules.staff_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => { navigate('/dashboard/ankuendigungen'); }}>
                <div className="module-icon">知らせ</div>
                <div className="module-content">
                  <h4>Ankündigungen</h4>
                  <p>Infos & Neuigkeiten für Teilnehmer</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('modus-uebersicht')} title="Modus = Wertungsart einer Disziplin (K.o.-Bracket, Pool jeder gegen jeden, Formen-Wertung …).">
                <div className="module-icon">競技</div>
                <div className="module-content">
                  <h4>Modus-Übersicht</h4>
                  <p>Anzeige-Einstellungen je Disziplin — Farben, Labels, Display-Layouts</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('events')}>
                <div className="module-icon">合宿</div>
                <div className="module-content">
                  <h4>Lehrgänge &amp; Camps</h4>
                  <p>Seminare, Prüfungen, Trainingscamps &amp; Visa-Briefe</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              {user?.isAdmin && (
                <React.Fragment key="admin-modules-org">
                  <div className="module-card admin" onClick={() => setActiveView('sports')}>
                    <div className="module-icon">競技</div>
                    <div className="module-content">
                      <h4>{t('admin.modules.multi_sport')}</h4>
                      <p>{t('admin.modules.multi_sport_sub')}</p>
                    </div>
                    <div className="module-arrow">→</div>
                  </div>

                  <div className="module-card admin" onClick={() => setActiveView('admin')}>
                    <div className="module-icon">管理</div>
                    <div className="module-content">
                      <h4>{t('admin.modules.administration')}</h4>
                      <p>{t('admin.modules.administration_sub')}</p>
                    </div>
                    <div className="module-arrow">→</div>
                  </div>
                </React.Fragment>
              )}
            </div>
          )}

          {/* Wettkampftag Tab */}
          {modulesTab === 'wettkampftag' && (
            <div className="modules-grid">
              <div className="module-card module-card--highlight" onClick={() => setShowSchnellreg(true)}>
                <div className="module-icon">⚡</div>
                <div className="module-content">
                  <h4>Schnellregistrierung</h4>
                  <p>Express · Kategorie-zuerst · Vereinsliste · QR Self-Service</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('wettkaempfer-registrieren')}>
                <div className="module-icon">登録</div>
                <div className="module-content">
                  <h4>{t('admin.modules.register_athletes')}</h4>
                  <p>{t('admin.modules.register_athletes_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('wettkaempfer')}>
                <div className="module-icon">選手</div>
                <div className="module-content">
                  <h4>{t('admin.modules.athletes')}</h4>
                  <p>{t('admin.modules.athletes_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              {/* Aufgabe 138 (02.10.2026): die turnierübergreifende Liste hatte
                  keinen Einstieg mehr — nur über die Adresse erreichbar. */}
              <div className="module-card" onClick={() => setActiveView('wettkaempfer-alle')}
                title="Alle Wettkämpfer des Verbands, unabhängig vom gewählten Turnier">
                <div className="module-icon">全員</div>
                <div className="module-content">
                  <h4>Alle Wettkämpfer</h4>
                  <p>Turnierübergreifend: alle Athleten des Verbands</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('anmeldungen')}>
                <div className="module-icon">申込</div>
                <div className="module-content">
                  <h4>{t('admin.modules.registrations')}</h4>
                  <p>{t('admin.modules.registrations_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('einzelstarter')} title="Einzelstarter = jemand, der in seiner Division keinen Gegner hat. Hier wird entschieden, wohin er wechselt.">
                <div className="module-icon">単独</div>
                <div className="module-content">
                  <h4>Einzelstarter &amp; kleine Klassen</h4>
                  <p>Wer steht allein? Umlegen, Verein informieren, kleine Klassen zusammenlegen</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('vereinsmails')}>
                <div className="module-icon">連絡</div>
                <div className="module-content">
                  <h4>Mails an Vereine</h4>
                  <p>Anmeldebestätigung und Turnierinfo mit Matte und Uhrzeit</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('checkin')}>
                <div className="module-icon">受付</div>
                <div className="module-content">
                  <h4>Check-in</h4>
                  <p>Wer ist da? Anwesenheit &amp; Zahlung vor Ort</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              {/* ⚠️ Die Ansage-Station laeuft in einem EIGENEN Fenster, nicht als
                  Ansicht im Dashboard: sie muss den ganzen Turniertag offen
                  bleiben und darf nicht verschwinden, weil jemand
                  weiterklickt. */}
              <div
                className="module-card"
                onClick={() => window.open(`/ansage/${selectedTurnier?.turnier_id || selectedTurnier?.id}`, '_blank')}
              >
                <div className="module-icon">放送</div>
                <div className="module-content">
                  <h4>Ansage-Station</h4>
                  <p>Spricht die Aufrufe über die Lautsprecheranlage</p>
                </div>
                <div className="module-arrow">↗</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('einwaage')} title="Einwaage = Wiegen am Turniertag. Wer nicht in seine Gewichtsklasse passt, wechselt die Division.">
                <div className="module-icon">計量</div>
                <div className="module-content">
                  <h4>Einwaage</h4>
                  <p>Gewichtskontrolle & Weigh-In</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('akkreditierung')}>
                <div className="module-icon">認証</div>
                <div className="module-content">
                  <h4>Akkreditierung</h4>
                  <p>Ausweise & Startberechtigung</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('druck')}>
                <div className="module-icon">印刷</div>
                <div className="module-content">
                  <h4>Druck</h4>
                  <p>Listen, Pläne & Ausdrucke</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card module-card--shortcut" onClick={() => setActiveView('modus-uebersicht')}>
                <div className="module-icon" style={{ opacity: 0.7, fontSize: '1.4rem' }}>競技</div>
                <div className="module-content">
                  <h4 style={{ fontSize: '0.9rem' }}>Display-Einstellungen</h4>
                  <p>Schnellzugriff → Anzeige je Disziplin anpassen</p>
                </div>
                <div className="module-arrow" style={{ opacity: 0.5 }}>↗</div>
              </div>

              <div className="module-card module-card--shortcut" onClick={() => { setStaffTab('reghelfer'); setActiveView('staff-management'); }}>
                <div className="module-icon" style={{ opacity: 0.8 }}>⚡</div>
                <div className="module-content">
                  <h4 style={{ fontSize: '0.9rem' }}>Registrierungshelfer</h4>
                  <p>Helfer-Accounts für den Wettkampftag verwalten</p>
                </div>
                <div className="module-arrow" style={{ opacity: 0.5 }}>↗</div>
              </div>

              <div className="module-card module-card--shortcut" onClick={() => { setStaffTab('kampfrichter'); setActiveView('staff-management'); }}>
                <div className="module-icon" style={{ opacity: 0.8 }}>🥋</div>
                <div className="module-content">
                  <h4 style={{ fontSize: '0.9rem' }}>Kampfrichter</h4>
                  <p>Kampfrichter-Accounts für den Wettkampftag</p>
                </div>
                <div className="module-arrow" style={{ opacity: 0.5 }}>↗</div>
              </div>
            </div>
          )}

          {/* Wettkampf Tab */}
          {modulesTab === 'wettkampf' && (
            <div className="modules-grid">
              <div className="module-card" onClick={() => setActiveView('brackets')} title="Bracket = der Turnierbaum einer Division: wer gegen wen kämpft und wer weiterkommt (K.o.-System).">
                <div className="module-icon">組合</div>
                <div className="module-content">
                  <h4>{t('admin.modules.brackets')}</h4>
                  <p>{t('admin.modules.brackets_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('modus-uebersicht')} title="Modus = Wertungsart einer Disziplin (K.o.-Bracket, Pool jeder gegen jeden, Formen-Wertung …).">
                <div className="module-icon">競技</div>
                <div className="module-content">
                  <h4>Modus-Übersicht</h4>
                  <p>Pointfighting, Kumite, Grappling, Formen & mehr</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('formen')} title="Formen (Kata) = vorgeführte Technikfolgen, von Kampfrichtern mit Noten bewertet. Hier werden Kategorien und Startreihenfolge verwaltet.">
                <div className="module-icon">型</div>
                <div className="module-content">
                  <h4>{t('admin.modules.forms')}</h4>
                  <p>{t('admin.modules.forms_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('selbstverteidigung')}>
                <div className="module-icon">護身</div>
                <div className="module-content">
                  <h4>Selbstverteidigung</h4>
                  <p>SV-Kategorien &amp; Wertung</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('zeitplan')} title="Zeitplan = wann welche Division auf welcher Fläche dran ist.">
                <div className="module-icon">予定</div>
                <div className="module-content">
                  <h4>{t('admin.modules.schedule')}</h4>
                  <p>{t('admin.modules.schedule_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('matten-wettkampf')}>
                <div className="module-icon">試合場</div>
                <div className="module-content">
                  <h4>Mattenfläche</h4>
                  <p>Matten & Schreiber-Übersicht</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('bruchtest')}>
                <div className="module-icon">試割</div>
                <div className="module-content">
                  <h4>{t('admin.modules.breaking')}</h4>
                  <p>{t('admin.modules.breaking_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('streaming')}>
                <div className="module-icon">配信</div>
                <div className="module-content">
                  <h4>{t('admin.modules.streaming')}</h4>
                  <p>{t('admin.modules.streaming_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => selectedTurnier && window.open(`/aufruf/${selectedTurnier.turnier_id}`, '_blank', 'width=1600,height=960,menubar=no,toolbar=no,location=no,status=no')}>
                <div className="module-icon">呼</div>
                <div className="module-content">
                  <h4>Aufruf-Display</h4>
                  <p>Kämpfer-Aufruf auf Zweitmonitor – alle Tatamis nebeneinander</p>
                </div>
                <div className="module-arrow">↗</div>
              </div>

              <div className="module-card" onClick={() => selectedTurnier && window.open(`/aufruf-control/${selectedTurnier.turnier_id}`, '_blank', 'width=600,height=900,menubar=no,toolbar=no,location=no,status=no')}>
                <div className="module-icon">🎙</div>
                <div className="module-content">
                  <h4>Schreiber-Steuerung</h4>
                  <p>Kategorien manuell aufrufen – steuert das Aufruf-Display</p>
                </div>
                <div className="module-arrow">↗</div>
              </div>
            </div>
          )}

          {/* Ergebnisse Tab */}
          {modulesTab === 'ergebnisse' && (
            <div className="modules-grid">
              <div className="module-card" onClick={() => setActiveView('platzierung')} title="Platzierung = die Plätze 1–3 je Division, aus Brackets und Formen-Wertung.">
                <div className="module-icon">順位</div>
                <div className="module-content">
                  <h4>{t('admin.modules.find_placement')}</h4>
                  <p>Platzierungen abrufen & prüfen</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('ergebnisse')}>
                <div className="module-icon">結果</div>
                <div className="module-content">
                  <h4>Gesamtwertung</h4>
                  <p>Punkte je Athlet über mehrere Kategorien</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('siegerehrung')}>
                <div className="module-icon">表彰</div>
                <div className="module-content">
                  <h4>{t('admin.modules.awards')}</h4>
                  <p>Siegerehrung & Podium-Anzeige</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('live-ergebnisse')}>
                <div className="module-icon">速報</div>
                <div className="module-content">
                  <h4>{t('admin.modules.live_results')}</h4>
                  <p>{t('admin.modules.live_results_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('urkunden')}>
                <div className="module-icon">賞状</div>
                <div className="module-content">
                  <h4>{t('admin.modules.certificates')}</h4>
                  <p>{t('admin.modules.certificates_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('saisonrangliste')}>
                <div className="module-icon">年間</div>
                <div className="module-content">
                  <h4>{t('admin.modules.season_ranking')}</h4>
                  <p>{t('admin.modules.season_ranking_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>

              <div className="module-card" onClick={() => setActiveView('analytics')}>
                <div className="module-icon">統計</div>
                <div className="module-content">
                  <h4>{t('admin.modules.statistics')}</h4>
                  <p>{t('admin.modules.statistics_sub')}</p>
                </div>
                <div className="module-arrow">→</div>
              </div>
            </div>
          )}

        </div>

      </div>

      {/* SAS Lizenzen & Verbände → jetzt unter /admin */}

      {/* Tagesbriefing-Modal */}
      {showBriefing && briefing && (
        <div className="briefing-overlay" onClick={() => {
          localStorage.setItem(`tda-briefing-${new Date().toISOString().slice(0,10)}`, '1');
          setShowBriefing(false);
        }}>
          <div className="briefing-modal" onClick={e => e.stopPropagation()}>
            <div className="briefing-header">
              <div className="briefing-title">
                <span className="briefing-icon">📋</span>
                <div>
                  <h3>{(() => { const h = new Date().getHours(); return h < 12 ? 'Guten Morgen!' : h < 18 ? 'Guten Tag!' : 'Guten Abend!'; })()}</h3>
                  <p>Offene Punkte für das nächste Turnier</p>
                </div>
              </div>
              <button className="briefing-close" onClick={() => {
                localStorage.setItem(`tda-briefing-${new Date().toISOString().slice(0,10)}`, '1');
                setShowBriefing(false);
              }}>✕</button>
            </div>
            <div className="briefing-body">
              {briefing.map(item => (
                <div
                  key={item.key}
                  className={`briefing-item ${item.done ? 'done' : 'open'}`}
                  onClick={() => {
                    if (item.done) return;
                    localStorage.setItem(`tda-briefing-${new Date().toISOString().slice(0,10)}`, '1');
                    setShowBriefing(false);
                    setActiveView(item.link);
                  }}
                >
                  <span className="briefing-check">{item.done ? '✅' : '⬜'}</span>
                  <span className="briefing-label">{item.label}</span>
                  {item.count > 0 && (
                    <span className={`briefing-badge ${item.invertCount ? 'badge-warn' : 'badge-ok'}`}>
                      {item.invertCount ? `${item.count} offen` : item.count}
                    </span>
                  )}
                  {!item.done && <span className="briefing-arrow">→</span>}
                </div>
              ))}
            </div>
            <div className="briefing-footer">
              <button className="briefing-dismiss" onClick={() => {
                localStorage.setItem(`tda-briefing-${new Date().toISOString().slice(0,10)}`, '1');
                setShowBriefing(false);
              }}>Schließen — heute nicht mehr anzeigen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Modernes Turnier-Auswahlfeld

// ── System & Info Panel ─────────────────────────────────
function SystemInfoPanel({ version, buildDate, changelog }) {
  const typeConfig = {
    major:       { label: 'Major Update', color: '#e67e22' },
    feature:     { label: 'Neue Funktion', color: '#2ecc71' },
    improvement: { label: 'Verbesserung', color: '#3498db' },
    bugfix:      { label: 'Bugfix', color: '#e74c3c' },
    security:    { label: 'Sicherheit', color: '#9b59b6' },
  };
  return (
    <div className="system-info-panel">
      <div className="system-info-header">
        <h2>⚙️ System & Info</h2>
        <div className="system-info-meta">
          <span className="si-version">v{version}</span>
          <span className="si-build">Build {buildDate}</span>
        </div>
      </div>
      <div className="system-info-body">
        <div className="si-section">
          <h3>Plattform</h3>
          <div className="si-grid">
            <div className="si-row"><span>Version</span><strong>v{version}</strong></div>
            <div className="si-row"><span>Build-Datum</span><strong>{buildDate}</strong></div>
            <div className="si-row"><span>Umgebung</span><strong>Production</strong></div>
            <div className="si-row"><span>Backend</span><strong>Node.js / Express / MySQL</strong></div>
            <div className="si-row"><span>Frontend</span><strong>React (CRA)</strong></div>
          </div>
        </div>
        <div className="si-section">
          <h3>Vollständiger Changelog</h3>
          <div className="changelog-list">
            {changelog.map((entry, i) => {
              const tc = typeConfig[entry.type] || { label: entry.type, color: '#888' };
              return (
                <div key={i} className={`cl-entry ${i === 0 ? 'cl-entry--latest' : ''}`}>
                  <div className="cl-entry-header">
                    <span className="cl-version">v{entry.version}</span>
                    <span className="cl-type-badge" style={{ background: tc.color }}>{tc.label}</span>
                    <span className="cl-date">{entry.date}</span>
                  </div>
                  <div className="cl-title">{entry.title}</div>
                  <ul className="cl-highlights">
                    {entry.highlights.map((h, j) => <li key={j}>{h}</li>)}
                  </ul>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Audit-Log Panel ──────────────────────────────────────
function AuditPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    fetch('/api/dashboard/audit-log', { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : { data: [] })
      .then(d => { setLogs(d.data || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = logs.filter(l =>
    !filter || l.action?.includes(filter) || l.user?.includes(filter) || l.details?.includes(filter)
  );

  return (
    <div className="audit-panel">
      <div className="audit-header">
        <h2>📋 Audit-Log</h2>
        <input
          className="audit-filter"
          placeholder="Filtern…"
          value={filter}
          onChange={e => setFilter(e.target.value)}
        />
      </div>
      {loading ? (
        <div className="audit-loading">Lade…</div>
      ) : filtered.length === 0 ? (
        <div className="audit-empty">Keine Einträge vorhanden.</div>
      ) : (
        <div className="audit-table-wrap">
          <table className="audit-table">
            <thead>
              <tr><th>Zeit</th><th>Benutzer</th><th>Aktion</th><th>Details</th></tr>
            </thead>
            <tbody>
              {filtered.map((l, i) => (
                <tr key={i}>
                  <td className="audit-td-time">{l.ts ? new Date(l.ts).toLocaleString('de-DE') : '—'}</td>
                  <td>{l.user || '—'}</td>
                  <td><span className="audit-action">{l.action || '—'}</span></td>
                  <td className="audit-td-details">{l.details || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default MainDashboard;
